import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Shield, ChevronLeft, ChevronRight, MessageCircle, Phone, X, Settings, Building2, ChevronDown, Search } from 'lucide-react';
import { DashboardSkeleton } from './Skeleton';
import FinancialDashboard from './FinancialDashboard';
import ChatPanel from '../Chat/ChatPanel';
import PhoneSettings from '../Settings/PhoneSettings';
import { api } from '../../utils/api';
import './Dashboard.css';
import logoImg from '../../assets/logo.png';

function Dashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [chatCollapsed, setChatCollapsed] = useState(false);
  const [showPhoneSettings, setShowPhoneSettings] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState(null);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [showWhatsAppDropdown, setShowWhatsAppDropdown] = useState(false);
  const [unlinking, setUnlinking] = useState(false);
  const whatsappDropdownRef = useRef(null);
  const [businesses, setBusinesses] = useState([]);
  const [activeBusinessId, setActiveBusinessId] = useState(null);
  const [showBusinessDropdown, setShowBusinessDropdown] = useState(false);
  const [businessSearch, setBusinessSearch] = useState('');
  const businessDropdownRef = useRef(null);
  const [switchingBusiness, setSwitchingBusiness] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    // Load user data from API (including is_admin, phone, etc.)
    loadUserData();
    loadDashboard();

    // Silently refresh token every 20 minutes to prevent session expiry
    const refreshInterval = setInterval(() => {
      api.refreshToken().catch(() => {
        // Refresh failed — token may have expired, redirect handled by handleAuthError
      });
    }, 20 * 60 * 1000);

    return () => clearInterval(refreshInterval);
  }, [navigate]);

  const loadUserData = async () => {
    try {
      const me = await api.getMe();
      setUser(me);
      setPhoneNumber(me.phone_number);
      setPhoneVerified(me.phone_verified);
      setBusinesses(me.businesses || []);
      setActiveBusinessId(me.active_business_id);
    } catch (error) {
      // Silently fail — user will see unauthenticated state
    }
  };

  const handlePhoneUpdated = (newPhone, verified) => {
    setPhoneNumber(newPhone);
    setPhoneVerified(verified);
  };

  const handleSwitchBusiness = async (bizId) => {
    if (bizId === activeBusinessId || switchingBusiness) return;
    setSwitchingBusiness(true);
    try {
      await api.switchBusiness(bizId);
      setActiveBusinessId(bizId);
      setShowBusinessDropdown(false);
      // Reload dashboard data and chat for the new business
      loadDashboard();
    } catch (err) {
      // Silently fail
    } finally {
      setSwitchingBusiness(false);
    }
  };

  const handleDisconnectWhatsApp = async () => {
    setUnlinking(true);
    try {
      await api.unlinkPhone();
      setPhoneNumber(null);
      setPhoneVerified(false);
      setShowWhatsAppDropdown(false);
    } catch (err) {
      // Silently fail — UI stays in connected state
    } finally {
      setUnlinking(false);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (whatsappDropdownRef.current && !whatsappDropdownRef.current.contains(e.target)) {
        setShowWhatsAppDropdown(false);
      }
    };
    if (showWhatsAppDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showWhatsAppDropdown]);

  // Close business dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (businessDropdownRef.current && !businessDropdownRef.current.contains(e.target)) {
        setShowBusinessDropdown(false);
      }
    };
    if (showBusinessDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showBusinessDropdown]);

  const formatPhone = (phone) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }
    if (cleaned.length === 10) {
      return `+1 (${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const data = await api.getFinancialDashboard();
      setDashboardData(data);
      setConnected(!!(data && data.has_data));
    } catch (error) {
      setConnected(false);
      setDashboardData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  const connectGoogleSheets = () => {
    alert('To connect Google Sheets, please contact your administrator to set up your data source.');
  };

  return (
    <div className="dashboard-container">
      {/* Header */}
      <header className="dashboard-header">
        <div className="header-left">
          <img src={logoImg} alt="Kigitz" className="header-logo" />
          <span className="user-email">{user?.email}</span>
          {/* Business Selector */}
          {businesses.length > 1 && (
            <div className="business-selector-wrapper" ref={businessDropdownRef}>
              <button
                className="business-selector-btn"
                onClick={() => { setBusinessSearch(''); setShowBusinessDropdown(!showBusinessDropdown); }}
                disabled={switchingBusiness}
              >
                <Building2 size={16} />
                <span className="business-name-label">
                  {businesses.find(b => b.id === activeBusinessId)?.name || 'Select Business'}
                </span>
                <ChevronDown size={14} className={showBusinessDropdown ? 'chevron-rotated' : ''} />
              </button>
              {showBusinessDropdown && (
                <div className="business-dropdown">
                  <div className="business-dropdown-search">
                    <Search size={14} />
                    <input
                      type="text"
                      placeholder="Search businesses..."
                      value={businessSearch}
                      onChange={(e) => setBusinessSearch(e.target.value)}
                      autoFocus
                    />
                  </div>
                  {(() => {
                    const q = businessSearch.trim().toLowerCase();
                    const list = businesses.filter(b => (b.name || '').toLowerCase().includes(q));
                    return (
                      <div className="business-dropdown-list">
                        {list.map(biz => (
                          <button
                            key={biz.id}
                            className={`business-dropdown-item ${biz.id === activeBusinessId ? 'active' : ''}`}
                            onClick={() => handleSwitchBusiness(biz.id)}
                          >
                            <Building2 size={14} />
                            <span className="business-item-name">{biz.name}</span>
                            {biz.id === activeBusinessId && <span className="active-check">✓</span>}
                          </button>
                        ))}
                        {list.length === 0 && (
                          <div className="business-dropdown-empty">No businesses match your search</div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="header-right">
          {phoneVerified && phoneNumber && (
            <div className="whatsapp-header-wrapper" ref={whatsappDropdownRef}>
              <button
                className="header-icon-btn whatsapp-connected"
                onClick={() => setShowWhatsAppDropdown(!showWhatsAppDropdown)}
                title={`WhatsApp: ${formatPhone(phoneNumber)}`}
                aria-label="WhatsApp settings"
              >
                <MessageCircle size={18} />
                <span className="whatsapp-phone-label">{formatPhone(phoneNumber)}</span>
              </button>
              {showWhatsAppDropdown && (
                <div className="whatsapp-dropdown">
                  <div className="whatsapp-dropdown-header">
                    <MessageCircle size={16} />
                    <span>WhatsApp Connected</span>
                  </div>
                  <div className="whatsapp-dropdown-phone">
                    <Phone size={14} />
                    <span>{formatPhone(phoneNumber)}</span>
                  </div>
                  <button
                    className="whatsapp-settings-btn"
                    onClick={() => { setShowWhatsAppDropdown(false); setShowPhoneSettings(true); }}
                  >
                    <Settings size={14} />
                    Notification Settings
                  </button>
                  <button
                    className="whatsapp-disconnect-btn"
                    onClick={handleDisconnectWhatsApp}
                    disabled={unlinking}
                  >
                    <X size={14} />
                    {unlinking ? 'Disconnecting...' : 'Disconnect'}
                  </button>
                </div>
              )}
            </div>
          )}
          {user?.is_admin && (
            <button
              className="header-icon-btn"
              onClick={() => navigate('/admin')}
              title="Admin"
              aria-label="Admin dashboard"
            >
              <Shield size={20} />
            </button>
          )}
          <button className="logout-btn" onClick={handleLogout}>
            <LogOut size={18} />
            Logout
          </button>
        </div>
      </header>

      {/* Split Layout: Chat Left | Metrics Right */}
      <div className="dashboard-split-layout">
        {/* Left: AI Chat Panel */}
            <aside className={`chat-sidebar-left ${chatCollapsed ? 'collapsed' : ''}`}>
            <button
                className="collapse-toggle-left"
                onClick={() => setChatCollapsed(!chatCollapsed)}
                title={chatCollapsed ? 'Expand Chat' : 'Collapse Chat'}
                aria-label={chatCollapsed ? 'Expand chat panel' : 'Collapse chat panel'}
            >
                {chatCollapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
            </button>
            
            {!chatCollapsed && !phoneVerified && (
              <div className="whatsapp-banner whatsapp-banner-chat">
                <div className="banner-content">
                  <MessageCircle size={22} className="whatsapp-icon" />
                  <div>
                    <strong>Chat with Kigi on the go</strong>
                    <p>Connect WhatsApp to ask questions about your finances anytime</p>
                  </div>
                </div>
                <button className="btn-whatsapp" onClick={() => setShowPhoneSettings(true)}>
                  Connect WhatsApp
                </button>
              </div>
            )}

            {!chatCollapsed && <ChatPanel key={activeBusinessId} activeBusinessId={activeBusinessId} />}
            </aside>

        {/* Right: Metrics Panel */}
        <main className="metrics-panel">
          {/* Connection Banner - only show after loading completes and data is not connected */}
          {!loading && !connected && (
            <div className="connection-banner">
              <div className="banner-content">
                <span className="banner-icon">⚠️</span>
                <div>
                  <strong>Data Source Not Connected</strong>
                  <p>Connect your Google Sheet to view real financial data</p>
                </div>
              </div>
              <button className="btn-connect" onClick={connectGoogleSheets}>
                Connect Data
              </button>
            </div>
          )}

          {/* Financial Dashboard (Kigi charts) */}
          {loading ? (
            <DashboardSkeleton />
          ) : (
            <div className="fin-dash-embed">
              <FinancialDashboard data={dashboardData && dashboardData.has_data ? dashboardData : null} />
            </div>
          )}
        </main>
      </div>

      {/* Phone Settings Modal */}
      <PhoneSettings
        isOpen={showPhoneSettings}
        onClose={() => setShowPhoneSettings(false)}
        userPhone={phoneNumber}
        phoneVerified={phoneVerified}
        onPhoneUpdated={handlePhoneUpdated}
      />
    </div>
  );
}

export default Dashboard;
